import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import {
    GridComponent,
    IGridActionsRowButton,
    IGridColumn,
    IGridSettings,
    IPagedData,
    ITableSettings
} from "@features/grid";
import {
    DashboardQueryPageRequest,
    IDashboardQueryBuild,
    IDashboardQueryMinimal,
    IDashboardQueryView,
    IDashboardWidgetBuild
} from "@main/reporting.v3/dashboard/dashboard-models";
import { DashboardQueryService } from "@main/reporting.v3/dashboard/api/dashboard-query.service";
import { QueryBuilderComponent } from "@main/reporting.v3/query-builder/components/query-builder.component";
import { ConfirmationService, MessageService, SelectItem, SelectItemGroup } from "primeng/api";
import { SqlQueryBuilderService } from "@main/reporting.v3/query-builder/api/sql-query-builder.service";
import { IWidgetSource, QueryAccessLevel } from "@main/reporting.v3/core/reporting-models";
import { QueryCommand } from "@features/filter";
import { Message } from "@bbwt/classes";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";


@Component({
    selector: "reporting-dashboard-queries",
    templateUrl: "./dashboard-queries.component.html",
    styleUrls: ["./dashboard-queries.component.scss"]
})
export class ReportingDashboardQueriesComponent implements OnDestroy {
    tableSettings: ITableSettings = {
        columns: <IGridColumn[]>[
            {
                field: "queryName",
                header: "Query name",
                serverFieldName: "querySource.name",
            },
            {
                field: "widgets",
                header: "Usage",
                sortable: false,
            }
        ]
    }

    gridSettings: IGridSettings = {
        readonly: true,
        dataService: this,
        dataServiceGetPageMethodName: this.requestData.name,
        additionalRowActions: [
            <IGridActionsRowButton>{
                buttonClass: "p-button-text",
                label: "Edit",
                hint: "Open query edition dialog",
                handler: this.editQuery.bind(this),
            },
            <IGridActionsRowButton>{
                buttonClass: "p-button-text p-button-danger",
                label: "Delete",
                hint: "Deletes the query",
                handler: this.deleteQuery.bind(this),
                disabled: (query: IDashboardQueryView): boolean => !!query.widgets?.length,
            }
        ],
    }

    // General settings.
    dashboardQueries: IDashboardQueryMinimal[] = [];
    loading: boolean = false;

    // Query edition settings.
    createdQueries: IDashboardQueryBuild[] = [];
    editedQueries: IDashboardQueryBuild[] = [];
    removedQueries: IDashboardQueryView[] = [];

    // Query edition dialog settings.
    displayQueryBuilder: boolean;
    dashboardQueryEditing: IDashboardQueryView;

    // Dashboard query options of this dashboard.
    @Input() querySourceOptions: SelectItemGroup[] = [];
    @Input() dashboardWidgets: IDashboardWidgetBuild[] = [];

    @Output() readonly querySourceOptionsChange: EventEmitter<SelectItemGroup[]> = new EventEmitter<SelectItemGroup[]>();

    private _dashboardId: string;
    protected readonly QueryAccessLevel = QueryAccessLevel;

    @ViewChild(GridComponent) private grid: GridComponent;
    @ViewChild(QueryBuilderComponent) private queryBuilder: QueryBuilderComponent;

    /* TODO: Change drafting logic to be bottom-up instead of top-down, to be able to request
        a query release from any component, sending the request to the core.
        Bottom-up: send the request to the core, and then the core sends the request to the corresponding
                   query service using providers.
        Top-down (current one): send the request directly to corresponding service, and then the latter
                   sends the request to the core. */
    constructor(private readonly dashboardQueryService: DashboardQueryService,
                private readonly sqlQueryBuilderService: SqlQueryBuilderService,
                private readonly confirmationService: ConfirmationService,
                private readonly messageService: MessageService,
                private readonly sanitizer: DomSanitizer) {
    }

    @Input() set dashboardId(value: string) {
        if (!value) return;
        this._dashboardId = value;
        this.refreshDashboardQueries().then();
    }

    get dashboardId(): string {
        return this._dashboardId;
    }

    get queryBuilderDisabled(): boolean {
        return !this.queryBuilder || this.queryBuilder.disabled;
    }

    get editQueryDialogHeader(): string {
        if (!this.dashboardQueryEditing) return "New Query";

        return !this.dashboardQueryEditing.queryName
            ? "Edit query"
            : `Edit query • ${this.dashboardQueryEditing.queryName}`;
    }

    ngOnDestroy(): void {
        this.cancel().then();
    }

    // Refreshing methods.
    private async refreshDashboardQueries(): Promise<void> {
        /* TODO: Responsibility for refreshing the dashboard grid has been left to the dashboard,
            because there is no correct way to fill the 'usages' column correctly without the current patch.
            This needs to be corrected when dashboard drafting is implemented. */
        this.refreshDashboardQueriesOptions().then();
    }

    private async refreshDashboardQueriesOptions(): Promise<void> {
        // Customize request to omit removed queries.
        const request: DashboardQueryPageRequest = this.getPageRequestSettings();

        this.dashboardQueries = await this.dashboardQueryService.getAllByDashboardId(this.dashboardId, request);

        // Create a select item for each dashboard query.
        this.querySourceOptions = [<SelectItemGroup>{
            label: "Report queries",
            items: this.dashboardQueries.map(query =>
                <SelectItem>{
                    label: query.queryName,
                    value: query.querySourceId
                })
        }];

        this.querySourceOptionsChange.emit(this.querySourceOptions);
    }

    public refreshDashboardQueriesGrid(): void {
        this.grid?.reload().then();
    }

    private async requestData(queryCommand: QueryCommand): Promise<IPagedData<IDashboardQueryView>> {
        if (!this.dashboardId) return { total: 0, items: [] };

        // Customize request to omit removed queries.
        const request: DashboardQueryPageRequest = this.getPageRequestSettings(queryCommand);

        // Return a page of the dashboard queries of this dashboard.
        return await this.dashboardQueryService.getPageByDashboardId(this.dashboardId, request);
    }

    // CRUD methods.
    private editQuery(query: IDashboardQueryView): void {
        if (query.queryType !== "sql") {
            this.messageService.add(Message.Info(`'${query.queryType}' query editing mode is not yet supported.`));
        } else {
            this.dashboardQueryEditing = query;
            this.displayQueryBuilder = true;
        }
    }

    private deleteQuery(query: IDashboardQueryView): void {
        this.confirmationService.confirm({
            message: `Are you sure that you want to delete query${query.queryName?.length ? ` "${query.queryName}"` : ""}?`,
            accept: () => {
                // Simulate removal of the query by adding it to removed queries list.
                this.removedQueries.push(query);
                this.messageService.add(Message.Success("Dashboard Query deleted."))
                this.refreshDashboardQueriesOptions().then();
                this.refreshDashboardQueriesGrid();
            }
        });
    }

    public async save(): Promise<void> {
        if (this.loading) return;
        this.loading = true;

        // Release all created queries (this will also delete
        // residual dashboard queries resulting from soft editions).
        await Promise.all(this.createdQueries.map(query =>
            this.sqlQueryBuilderService.releaseDraft(query.querySourceId)));

        // Remove all pending deleted queries.
        await Promise.all(this.removedQueries.map(query =>
            this.dashboardQueryService.delete(query.id, { showSuccessMessage: false })));

        this.createdQueries = [];
        this.removedQueries = [];
        this.editedQueries = [];
        this.loading = false;
    }

    public async cancel(): Promise<void> {
        if (this.loading) return;
        this.loading = true;

        // Delete all created dashboard queries.
        await Promise.all(this.createdQueries.map(query =>
            this.dashboardQueryService.delete(query.id, { showSuccessMessage: false })));

        this.createdQueries = [];
        this.removedQueries = [];
        this.editedQueries = [];
        this.loading = false;
    }

    // Dialog methods.
    protected onQueryCreate(): void {
        this.dashboardQueryEditing = null;
        this.displayQueryBuilder = true;
    }

    protected async onQueryBuilderSave(): Promise<void> {
        if (!this.queryBuilder) return;

        /* TODO: This is a patch, name validation should be done in the backend.
            When dashboard drafting is implemented this needs to be fixed.
            Restore approach of previous commits then. */
        const name: string = this.queryBuilder.querySource.name;
        const originalQuerySourceId: string = this.queryBuilder.querySourceId;
        const releasedQuerySourceId: string = this.queryBuilder.querySource?.releaseQueryId;
        if (this.dashboardQueries.some(query =>
            originalQuerySourceId !== query.querySourceId &&
            releasedQuerySourceId !== query.querySourceId &&
            !name.trim().localeCompare(query.queryName))) {
            return this.messageService.add(Message.Error("This query name is already used."));
        }

        // If this query is a draft, save the changes.
        // Otherwise, create a new draft for it.
        const querySourceId = await this.queryBuilder.saveDraft();
        if (!querySourceId) return;

        // If the query is not a draft already (is not currently in edition).
        if (!this.createdQueries.some(query => query.querySourceId === querySourceId) &&
            !this.editedQueries.some(query => query.querySourceId === querySourceId)) {

            // Create a new dashboard query draft for it.
            const createdQuery: IDashboardQueryBuild = await this.dashboardQueryService.create({
                id: null,
                querySourceId: querySourceId,
                dashboardId: this.dashboardId,
            }, { showSuccessMessage: false });

            // If this is an edited query, add old version to edited queries list.
            if (!!this.dashboardQueryEditing) this.editedQueries.push(this.dashboardQueryEditing);

            // Add new created query to created queries list.
            this.createdQueries.push(createdQuery);
        }

        this.refreshDashboardQueriesOptions().then();
        this.refreshDashboardQueriesGrid();
        this.onQueryBuilderClose();
    }

    protected async onQueryBuilderCancel(): Promise<void> {
        if (!this.queryBuilder) return;

        // Deletes the query only if it is a draft.
        await this.queryBuilder.cancelDraft();
        this.onQueryBuilderClose();
    }

    protected onQueryBuilderUpdatePreview(): void {
        if (!this.queryBuilder) return;

        // If this query is a draft, make the changes over the query.
        // Otherwise, create a new draft query for them.
        this.queryBuilder.saveDraft().then();
    }

    protected onQueryBuilderClose(): void {
        this.displayQueryBuilder = false;
        this.dashboardQueryEditing = null;
    }

    // Auxiliary methods.
    private getPageRequestSettings(queryCommand?: QueryCommand): DashboardQueryPageRequest {
        return {
            queryCommand: queryCommand,
            removedQueries: this.removedQueries.map(query => query["id_original"]),
            createdQueries: this.createdQueries.map(query => query["id_original"]),
            editedQueries: this.editedQueries.map(query => query["id_original"]),
            dashboardWidgets: this.dashboardWidgets.map(widget => widget.widgetSourceId)
        }
    }

    protected getQueryUsagesFormattedString(widgets: IWidgetSource[]): SafeHtml {
        if (!widgets?.length) return "";

        const widgetFormattedStrings: string[] = widgets
            .map(widget => this.getWidgetFormattedString(widget))
            .filter(widgetFormattedString => !!widgetFormattedString);

        return this.sanitizer.bypassSecurityTrustHtml(widgetFormattedStrings.join("<span>, </span>"));
    }

    private getWidgetFormattedString(widget: IWidgetSource): string {
        if (!widget?.widgetType) return null;

        let icon: string;
        switch (widget.widgetType) {
            case "chart":
                icon = "pi pi-chart-pie";
                break;
            case "table":
                icon = "pi pi-table";
                break;
            case "control-set":
                icon = "pi pi-minus-circle";
                break;
            case "html":
                icon = "pi pi-code";
                break;
        }

        const name: string = widget.title ?? widget.name ?? "&lt;anonymous widget&gt;";

        return `<span class="widget-name"><i class="${icon} widget-icon"></i>${name}</span>`;
    }
}